import React from 'react'
import { IEvent, WorkflowStatus } from '../../store/events/interfaces'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { checkRegIntegrity } from '../../functions/events/EventIntegrity'
import { addToast } from '../../store/misc/misc'
import { addRegistration, removeRegistration } from '../../store/registrations/registrations'
import { isTabletOrMobileQuery } from '../../utils'
import { useMediaQuery } from 'react-responsive'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { ToastType } from '../../store/misc/interfaces'
import { IProgram } from '../../store/program/interfaces'
import { IconName } from '@fortawesome/free-regular-svg-icons'

const DeniedReason = (props: {
  icon: IconName
  reason: string
}) => {
  const {icon, reason} = props
  return (
    <>
      <FontAwesomeIcon icon={icon} role="presentation" />
      &nbsp;{reason}
    </>
  )
}

export const checkEventRegistrationDenied = (program: IProgram, event: IEvent): JSX.Element | boolean => {
  const now = new Date()
  if (program.start_registration_date === null) {
    return <DeniedReason icon="calendar-times" reason="Kein Anmeldestart festgelegt" />
  }

  const isEventFull = event && (event.max_participants !== null && event.max_participants !== 0) && (event.registration_statistics.registration_amount_yes >= event.max_participants && event.registration_statistics.is_waiting_list_full)
  if (isEventFull) {
    return <DeniedReason icon="ban" reason="Voll" />
  }

  if (event.canceled) {
    return <DeniedReason icon="ban" reason="Abgesagt" />
  }

  if (event.has_external_registration) {
    return <DeniedReason icon="external-link-square-alt" reason="Externe Anmeldung" />
  }

  const isOver: boolean = Boolean(event?.end) && new Date(event?.end) < new Date()
  if (isOver) {
    return <DeniedReason icon="calendar-times" reason="Vorbei" />
  }

  // Only create Date object if we have a valid date string
  const firstRegDate = event.first_registration_date || program.start_registration_date
  const first_registration_date = firstRegDate ? new Date(firstRegDate) : null

  const lastRegDate = event.last_registration_date || program.last_registration_date
  const last_registration_date = lastRegDate ? new Date(lastRegDate) : null

  if (first_registration_date && now < first_registration_date) {
    return (
      <DeniedReason
        icon="calendar-times"
        reason={`Ab dem ${first_registration_date.toLocaleDateString('de-DE')}`}
      />
    )
  }

  if (last_registration_date && now > last_registration_date) {
    return <DeniedReason
      icon="calendar-times"
      reason="Anmeldefrist vorbei"
    />
  }

  return false
}


export const RegisterButton = (props: {
  editable: boolean
  event: IEvent
  extraClasses?: string
}) => {
  const { event, extraClasses, editable } = props

  const dispatch = useDispatch()
  const isTabletOrMobile = useMediaQuery(isTabletOrMobileQuery)
  const { loadedViaCode } = useSelector((state: RootState) => state.misc)
  const { data: program } = useSelector((state: RootState) => state.program)
  const { list: regs } = useSelector((state: RootState) => state.registrations)
  const {testMode } = useSelector((state: RootState) => state.institution)

  const { originalRegistrationIds } = useSelector(
    (state: RootState) => state.participant.data,
  )
  const isOriginalReg = originalRegistrationIds?.includes(event?.event_id)

  const isEventRegistrationCurrentlyDenied = checkEventRegistrationDenied(program, event)

  const isEventWaiting = (event.registration_statistics.is_max_participants_full && !event.registration_statistics.is_waiting_list_full)

  const hasRegistration = regs.some(
    reg => reg.event.event_id === event.event_id,
  )

  if (!(editable || program.is_participant_registration_allowed || testMode)) {
    return <></>
  }

  const buttonDisabled = (
    event.has_external_registration ||
    event.canceled ||
    (loadedViaCode && hasRegistration && isOriginalReg) ||
    !!isEventRegistrationCurrentlyDenied ||
    (program.use_workflow && (event.workflow_status !== WorkflowStatus.INSTITUTION_OK))
  )

  return (
    <Button
      variant={hasRegistration ? 'success' : 'info'}
      className={`${isTabletOrMobile ? 'mobile-btn' : 'w-100 h-100'} d-flex align-items-center justify-content-center ${extraClasses}`}
      disabled={
        testMode
          ? false
          : buttonDisabled
      }
      onClick={() => {
        if (regs.some((reg) => reg.event.event_id === event.event_id)) {
          dispatch(removeRegistration(event.event_id))
        } else {
          const issueToast = checkRegIntegrity(event, regs, program)
          if (issueToast) {
            dispatch(addToast(issueToast))
            if (
              issueToast.type === ToastType.Warning ||
              issueToast.type === ToastType.Info
            ) {
              dispatch(addRegistration(event))
            }
          } else {
            dispatch(addRegistration(event))
          }
        }
      }}
    >
      {!!isEventRegistrationCurrentlyDenied ? (
          <>{isEventRegistrationCurrentlyDenied}</>
      ) : loadedViaCode && hasRegistration && isOriginalReg ? (
        <>
          <FontAwesomeIcon icon="check-circle" role="presentation" />
          &nbsp;Bereits registriert
        </>
      ) : hasRegistration ? (
        <>
          <FontAwesomeIcon icon="minus-circle" role="presentation" />
          &nbsp;Abwählen
        </>
      ) : isEventWaiting ? (
        <>
          <FontAwesomeIcon icon="hourglass" role="presentation" />
          &nbsp;Warteliste
        </>
      ) : (
        <>
          <FontAwesomeIcon icon="plus-circle" />
          &nbsp;Auswählen
        </>
      )}
      {testMode && buttonDisabled ?
        <OverlayTrigger
          placement='top'
          overlay={
            <Tooltip id={`${event.id}-disabled-info`}>Diese Veranstaltung wäre für Teilnehmer im Moment nicht buchbar.</Tooltip>
          }
          trigger={['hover', 'focus']}
        >
          <FontAwesomeIcon icon={"warning"} style={{marginLeft: "5px"}}></FontAwesomeIcon>
        </OverlayTrigger>: ""}
    </Button>
  )
}
